import React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { STEP_STATUS } from '../constants';

/**
 * Single numbered step in the Copilot flow accordion.
 *
 * This is a plain, static show/hide implementation rather than
 * dock-design-kit's `AccordionItem`: that component measures its content's
 * height once to animate the expand/collapse, and doesn't re-measure when
 * the content inside grows afterward (e.g. adding more product cards while
 * the step is already open) - anything added past that initial height gets
 * silently clipped. A plain conditional render has no such height cap, so
 * the step's height always tracks its actual content.
 *
 * `status` mirrors the three visual states in the Figma spec:
 * - `pending` - not yet reached, muted grey avatar/title
 * - `active` - the current step, dark avatar/title
 * - `completed` - done, green-tinted header with a checkmark avatar and an
 *   optional `subtitle` summarizing what was selected/confirmed
 */
const AccordionStep = ({
  stepNumber,
  title,
  subtitle = null,
  isOpen = false,
  status = STEP_STATUS.PENDING,
  onToggle = () => {},
  children,
}) => {
  const isCompleted = status === STEP_STATUS.COMPLETED;
  const isActive = status === STEP_STATUS.ACTIVE;
  const isPending = status === STEP_STATUS.PENDING;

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-rounded-[12px] tw-border tw-border-solid tw-border-[#e5e7eb] tw-overflow-hidden'>
      <div
        className={`tw-flex tw-items-center tw-justify-between tw-p-[12px] tw-w-full ${
          isPending ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
        } ${
          isCompleted
            ? 'tw-bg-gradient-to-r tw-to-[#ECFDF3] tw-from-white'
            : 'tw-bg-white'
        }`}
        onClick={isPending ? undefined : onToggle}
        role='button'
        tabIndex={isPending ? -1 : 0}
      >
        <div className='tw-flex tw-items-center tw-gap-3 tw-min-w-0'>
          <div
            className={`tw-flex tw-items-center tw-justify-center tw-size-7 tw-rounded-full tw-shrink-0 tw-text-[12px] tw-leading-[16px] ${
              isActive
                ? 'tw-bg-grey-900 tw-text-white'
                : 'tw-bg-[#f9fafb] tw-border tw-border-solid tw-border-[#e5e7eb] tw-text-[#111927]'
            }`}
          >
            {isCompleted ? <Check size={14} /> : stepNumber}
          </div>
          <div className='tw-flex tw-flex-col tw-min-w-0 tw-gap-1'>
            <p
              className={`tw-text-[16px] tw-leading-[20px] tw-font-regular tw-m-0 tw-truncate ${
                isActive || isCompleted
                  ? 'tw-text-grey-800'
                  : 'tw-text-[#9da4ae]'
              }`}
            >
              {title}
            </p>
            {isCompleted && subtitle && (
              <p className='tw-text-[12px] tw-leading-[16px] tw-text-grey-700 tw-m-0 tw-truncate'>
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <ChevronDown
          size={24}
          className={`tw-shrink-0 tw-text-[#6c737f] tw-transition-transform tw-duration-200 ${
            isOpen ? 'tw-rotate-180' : ''
          }`}
        />
      </div>
      {isOpen && (
        <div className='tw-w-full tw-bg-white tw-pb-5 tw-px-5'>{children}</div>
      )}
    </div>
  );
};

export default AccordionStep;
